import {z} from 'zod';
import {
  HighlightTypeSchema,
  positionSchema,
} from '../video-highlight/types';

export const commentItemSchema = z
  .object({
    audioStartTime: z.number().min(0),
    audioEndTime: z.number().min(0),
    videoStartTime: z.number().min(0),
    videoEndTime: z.number().min(0),
  })
  .superRefine((comment, ctx) => {
    if (comment.audioEndTime <= comment.audioStartTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['audioEndTime'],
        message: 'audioEndTime must be greater than audioStartTime.',
      });
    }

    if (comment.videoEndTime < comment.videoStartTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['videoEndTime'],
        message: 'videoEndTime cannot be earlier than videoStartTime.',
      });
    }
  });

const mediaSourceSchema = z.object({
  url: z.string().min(1),
  duration: z.number().min(0).nullable().optional(),
});

export const timelineHighlightItemSchema = z
  .object({
    type: HighlightTypeSchema.default('rectangle'),
    audioStartTime: z.number().min(0),
    audioEndTime: z.number().min(0),
    videoStartTime: z.number().min(0).optional(),
    videoEndTime: z.number().min(0).optional(),
    position: positionSchema.optional(),
    borderRadius: z.number().min(0).optional(),
    borderWidth: z.number().min(1).max(50).optional(),
    borderColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    rotation: z.number().optional(),
    fadeIn: z.number().min(0).max(5).optional(),
    fadeOut: z.number().min(0).max(5).optional(),
    zoomScale: z.number().min(1).max(5).optional(),
    autoFitZoom: z.boolean().optional(),
    easingType: z
      .enum(['linear', 'ease-in', 'ease-out', 'ease-in-out'])
      .optional(),
  })
  .superRefine((highlight, ctx) => {
    if (highlight.type !== 'freeze' && highlight.position == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['position'],
        message: 'position is required unless type is freeze.',
      });
    }

    if (highlight.audioEndTime <= highlight.audioStartTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['audioEndTime'],
        message: 'audioEndTime must be greater than audioStartTime.',
      });
    }

    const hasVideoStart = highlight.videoStartTime != null;
    const hasVideoEnd = highlight.videoEndTime != null;
    if (hasVideoStart !== hasVideoEnd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: hasVideoStart ? ['videoEndTime'] : ['videoStartTime'],
        message: 'videoStartTime and videoEndTime must be provided together.',
      });
    }

    if (
      highlight.videoStartTime != null &&
      highlight.videoEndTime != null &&
      highlight.videoEndTime < highlight.videoStartTime
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['videoEndTime'],
        message: 'videoEndTime cannot be earlier than videoStartTime.',
      });
    }
  });

export type CommentItem = z.infer<typeof commentItemSchema>;
export type TimelineHighlightItem = z.infer<typeof timelineHighlightItemSchema>;

export const videoCommentaryHighlightSchema = z.object({
  backgroundVideo: mediaSourceSchema.extend({
    width: z.number().positive().nullable().optional(),
    height: z.number().positive().nullable().optional(),
    coordinateWidth: z.number().positive().nullable().optional(),
    coordinateHeight: z.number().positive().nullable().optional(),
    fps: z.number().positive().nullable().optional(),
  }),
  backgroundAudio: mediaSourceSchema,
  render: z
    .object({
      width: z.number().positive().nullable().optional(),
      height: z.number().positive().nullable().optional(),
    })
    .optional(),
  comments: z.array(commentItemSchema),
  highlights: z.array(timelineHighlightItemSchema).max(50),
  voiceVolume: z.number().min(0).max(100),
  originalVolume: z.number().min(0).max(100),
  defaultFadeIn: z.number().min(0).max(5).optional().default(0),
  defaultFadeOut: z.number().min(0).max(5).optional().default(0),
});

export type VideoCommentaryHighlightProps = Record<string, unknown> &
  z.infer<typeof videoCommentaryHighlightSchema>;
